// CallToAction.jsx - CTA sections for landing pages
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield } from 'lucide-react';

// Soft CTA for educational pages (Tier 1 & 2)
export function SoftCTA({
  headline = "This is why I'm building Pro IRP",
  description,
  linkTo = "/pricing",
  linkText = "Learn more about Pro IRP"
}) {
  return (
    <div className="mt-14 p-8 sm:p-10 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] rounded-2xl text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFB800]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-xs font-semibold mb-5">
          <Shield size={12} />
          From the Founder
        </div>
        <h3 className="text-xl sm:text-2xl font-bold mb-4">{headline}</h3>
        <p className="text-blue-100/80 leading-relaxed mb-6 max-w-2xl">
          {description ||
            `After years in this industry, I got tired of watching good agents lose good clients for preventable reasons.
            Pro IRP is the retention platform I wish I had—built specifically for Medicare agents who want to
            protect their book of business and stop the churn.`
          }
        </p>
        <Link
          to={linkTo}
          className="inline-flex items-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-6 py-3 rounded-xl hover:bg-[#e5a600] transition-all hover:scale-105 shadow-lg shadow-[#FFB800]/25 text-sm"
        >
          {linkText}
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

// Strong CTA for solution pages (Tier 3)
export function StrongCTA({
  headline = "Ready to stop losing clients?",
  description = "Join Medicare agents who are finally getting ahead of churn.",
  features = [
    "AI-powered risk scoring",
    "Automated retention outreach",
    "Real-time client alerts"
  ],
  primaryLink = "/signup",
  primaryText = "Start Free Trial",
  secondaryLink = "/pricing",
  secondaryText = "See Pricing"
}) {
  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB800]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
          <span className="w-2 h-2 bg-[#FFB800] rounded-full animate-pulse" />
          Join 500+ Medicare agents
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          {headline}
        </h2>
        <p className="text-xl text-blue-100/80 mb-8 max-w-2xl mx-auto leading-relaxed">
          {description}
        </p>

        {features.length > 0 && (
          <div className="flex flex-wrap justify-center gap-5 mb-10">
            {features.map((feature, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-2 text-sm text-blue-100/90"
              >
                <CheckCircle size={16} className="text-[#FFB800]" />
                {feature}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to={primaryLink}
            className="inline-flex items-center justify-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-8 py-4 rounded-xl hover:bg-[#e5a600] transition-all hover:scale-105 shadow-lg shadow-[#FFB800]/25"
          >
            {primaryText}
            <ArrowRight size={20} />
          </Link>
          <Link
            to={secondaryLink}
            className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors"
          >
            {secondaryText}
          </Link>
        </div>
        <p className="text-xs text-blue-200/40 mt-6">No credit card required</p>
      </div>
    </section>
  );
}

export default SoftCTA;
